'use client'

import { AllPurposeLink } from '@components/all-purpose-link'
import { articleRoute, journalRoute } from '@utils/routes'
import { BodyText, BrandText, DetailText, HeadlineText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import React from 'react'
import { ArticleDisplayProps } from '../types'
import { ArticlePageDetailsFragment } from '@/gql/generated/graphql'
import useMeasure from 'react-use-measure'
import { safeModulo } from '@utils/math'
import { PaginationButton, PaginationDirection } from '@components/pagination-button'
import { AnimatePresence, motion } from 'motion/react'
import { buttonVariants } from '@components/button'

const MoreArticleItem = ({ article }: { article: ArticlePageDetailsFragment | undefined | null }) => {
  const [frameRef, frameBounds] = useMeasure()
  const frameWidth = frameBounds.width
  const frameHeight = frameWidth / 1.77
  return (
    <AllPurposeLink href={articleRoute(article?.slug ?? '')}>
      <div className="h-full w-full" ref={frameRef}>
        <div
          className="relative"
          style={{
            width: `${frameWidth}px`,
            height: `${frameHeight}px`
          }}
        >
          <ImageWithCustomLoader
            src={article?.thumbnail?.data?.attributes?.url ?? ''}
            style={{
              objectFit: 'contain',
              objectPosition: 'center'
            }}
            fill
            alt={`Thumbnail for article: ${article?.title}`}
          />
        </div>
        <DetailText className="text-on-surface-inverted-variant mt-xs mb-2xs font-medium">
          {article?.category?.toUpperCase()}
        </DetailText>
        <BodyText className="text-on-surface-inverted">{article?.title}</BodyText>
      </div>
    </AllPurposeLink>
  )
}

export const MoreArticlesSection = ({ moreArticlesData }: ArticleDisplayProps): React.ReactElement => {
  const [currIndex, setCurrIndex] = React.useState<number>(0)

  const articles = moreArticlesData?.articles?.data

  const changeItem = (direction: PaginationDirection) => {
    // This is a hack to prevent the index from going out of bounds if the user goes
    // right then index should increase until it gets to the end and then it should be reset
    // to the beginning, if left then it should decrease until it gets to 0 then reset
    // to the end
    setCurrIndex(safeModulo(currIndex + (direction === PaginationDirection.Right ? 1 : -1), articles?.length ?? 0))
  }

  return (
    <div className="bg-surface-inverted py-xl px-md flex flex-col items-center justify-center">
      <HeadlineText className="text-on-surface-inverted font-light">Discover more articles</HeadlineText>
      <div className="py-xl gap-xl hidden grid-cols-2 md:grid md:py-[80px] lg:grid-cols-4">
        {articles?.map((a) => {
          const article = a.attributes
          if (!article) return null
          return (
            <div key={article?.slug}>
              <MoreArticleItem article={article} />
            </div>
          )
        })}
      </div>
      <div className="py-xl flex w-full items-center justify-between md:hidden">
        <PaginationButton
          direction={PaginationDirection.Left}
          onClick={() => changeItem(PaginationDirection.Left)}
          className="bg-primary-inverted-10 rounded-full"
        />
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3
            }}
            key={articles?.[currIndex]?.attributes?.slug}
            className="px-sm w-full"
          >
            {articles?.[currIndex]?.attributes ? <MoreArticleItem article={articles[currIndex].attributes} /> : null}
          </motion.div>
        </AnimatePresence>
        <PaginationButton
          direction={PaginationDirection.Right}
          onClick={() => changeItem(PaginationDirection.Right)}
          className="bg-primary-inverted-10 rounded-full"
        />
      </div>
      <AllPurposeLink
        href={journalRoute()}
        className={buttonVariants({ colorTheme: 'inverted-secondary', branded: true })}
      >
        <BrandText>VIEW ALL</BrandText>
      </AllPurposeLink>
    </div>
  )
}
